import { Button, Card, CardActions, CardContent, Typography, makeStyles } from '@material-ui/core';
import { PhoneAndroid } from '@material-ui/icons';
import { FormBuilder } from '@zennya/web-component-library/src';
import { MapBox } from 'components/AddressLocationForm/MapBox';
import useCustomModal from 'components/CustomConfirm';
import CustomConfirmDialog from 'components/CustomConfirm/CustomConfirmDialog';

import moment from "moment-timezone";
import { Breakdown } from 'pages/CustomerBookingCreate/4/ServiceBreakdown';
import { PageHeader } from 'pages/CustomerBookingCreate/components/PageHeader';
import { STEP_LOCATION, STEP_PAYMENT, STEP_PERSONAL_DETAILS, STEP_SCHEDULE, STEP_SERVICE_BREAKDOWN } from 'pages/CustomerBookingCreate/constants';
import { bookingState, stepState } from 'pages/CustomerBookingCreate/state';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { createOrUpdatePersonalInfo, updateClientProfile, useUserState } from 'services/authentication.service';
import { formatAddressObject } from 'util/format';
import { toasterror } from 'util/toasterror';
import { useTrackEvent } from 'util/trackEvent';
import * as Yup from 'yup';
const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(5),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1)
    }
  },
  cardContent: {
    "&>*": {
      marginBottom: theme.spacing(5),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1)
      }
    }
  },
  cardAction: {
    justifyContent: 'flex-end'
  }
}));

const PersonalDetailsScreen = ({ }) => {
  const classes = useStyles()

  const confirm = useCustomModal()
  const [step, setStep] = useRecoilState(stepState);
  const [booking, setBooking] = useRecoilState(bookingState);
  const [triggerSubmit, setTriggerSubmit] = useState(false);
  const { getUser } = useUserState()
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const fetchUserData = useCallback(
    async () => {
      setUser(await getUser(null, true))
    },
    [],
  );

  const history = useHistory()

  useEffect(
    () => {
      fetchUserData()
    }, []
  );

  const trackEvent = useTrackEvent()

  const form = useMemo(
    () => {
      const frm = {
        first_name: {
          type: 'text',
          label: 'First Name',
          fieldProps: {
            autoComplete: "off",
          },
          validator: () => Yup.string().required().nullable().label('First Name'),
        },
        last_name: {
          type: 'text',
          label: 'Last Name',
          fieldProps: {
            autoComplete: "off",
          },
          validator: () => Yup.string().required().nullable().label('Last Name'),
        },
        email: {
          type: 'email',
          label: 'Email',
          readOnly: true,
          fieldProps: {
            autoComplete: "off",
          },
          validator: () => Yup.string().email().required().nullable().label('Email'),
        },
        gender: {
          type: "radio",
          label: "Gender",
          initialValues: "",
          options: [
            { id: 'female', label: 'Female' },
            { id: 'male', label: 'Male' }
          ],
          settings: {
            labelField: "label",
            valueField: "id",
            inline: true,
          },
          validator: () => Yup.string().required().nullable().label(' '), //required
        },
        birthday: {
          type: 'date',
          label: 'Birthday',
          validator: () => Yup.date().required().nullable().label('Birthday'),
          disableFuture: true,
          disablePast: false,
          useLocalTime: true,
          fieldProps: {
            minDate: moment().subtract(120, 'y').toDate(),
            maxDate: moment().subtract(1, 'week').toDate()
          },
        },
        mobile_number: {
          type: 'phone',
          label: 'Mobile Number',
          initialValues: "",
          readOnly: !process.env.REACT_APP_ENABLE_MOBILE_VERIFICATION,
          validator: () => {
            const validator = Yup.string()
              .nullable()
              .matches(
                /^\+(63\d{10}|(?!63)\d{1,3}\d{10})$/,
                "invalid phone number format"
              )
              .label(" ")

            return !process.env.REACT_APP_ENABLE_MOBILE_VERIFICATION ? validator.required() : validator
          }
              ,
          icon: <PhoneAndroid />
        }
      }
      return frm
    }, []
  );

  const formLayout = useMemo(
    () => {
      return [
        ['first_name', 'last_name'],
        ['birthday', 'gender'],
        ['mobile_number', 'email']
      ]
    }, []
  );

  const onSubmit = useCallback(
    async ({
      first_name,
      last_name,
      birthday,
      gender,
      ...rest
    }) => {
      try {
        setLoading(true)
        const v = {
          first_name,
          last_name,
          birthday,
          gender
        }
        const res = await createOrUpdatePersonalInfo(user?.client?.id || user?.user?.id, v)
        setStep(STEP_LOCATION)
        history.replace(STEP_LOCATION, { internal: true })
        setLoading(false)
      } catch (error) {
        toasterror(error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details, {error})
        setLoading(false)
      }
    },
    [user],
  );

  const initValues = useMemo(
    () => {
      if (user?.personalInfo) {
        const v = {
          ...user?.personalInfo,
          birthday: user?.personalInfo?.birthday ? moment(user?.personalInfo?.birthday).toDate() : null,
          email: user?.client?.email || user?.user?.email,
          mobile_number: user?.client?.mobile_number || user?.user?.mobile_number,
          gender: user?.personalInfo?.gender || user?.client?.gender || user?.user?.gender || user?.gender
        }
        return v
      } else {
        const base = user?.client || user?.user
        const v = {
          first_name: base?.first_name,
          last_name: base?.last_name,
          email: base?.email,
          mobile_number: base?.mobile_number,
          gender: base?.gender,
          birthday: base?.birthday ? moment(base?.birthday).toDate() : null
        }
        return v
      }
    }, [user]
  );

  return (
    <>
      <PageHeader
        title="Personal Details"
        subheader='Please add your personal details below to complete your account creation.'
        backStep={STEP_SERVICE_BREAKDOWN}
        currentStep={STEP_PERSONAL_DETAILS}
        showBreadcrumbs
        loading={loading}
      >
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <FormBuilder
              variant="outlined"
              formFactor="default"
              hasReset={false}
              decouple
              setValues={initValues}
              triggerSubmit={triggerSubmit}
              onTriggerSubmit={v => {
                setTriggerSubmit(false)
              }}
              form={form}
              formLayout={formLayout}
              onSubmit={(v) => onSubmit(v)}

            />
          </CardContent>
          <Breakdown />
          <CardActions className={classes.cardAction}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                trackEvent('Create Booking Submit Personal Details')
                setTriggerSubmit(true)
              }}
            >
              Continue
            </Button>
          </CardActions>
        </Card>

      </PageHeader>
    </>
  )
}

export { PersonalDetailsScreen };
